import React, { Fragment, useState, useEffect } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import PortalMenu from "../Common/PortalMenu";
import {
 Row,
 Col,
 Card,
 Badge,
 Dropdown,
 Modal,
 Button,
 Table,
} from "react-bootstrap";
import AppPagination from "../Common/AppPagination";
import { Link } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import api, { getFileUrl } from "../../../api";
import swal from "sweetalert";
import { useSearchParams } from "react-router-dom";
import FilePreview from "../Common/FilePreview";
import SignatureModal from "../Common/SignatureModal";
import { useSelector } from "react-redux";
import PermissionWrapper from "../PermissionWrapper";
import VerifiedCopyActions from "../Common/VerifiedCopyActions";

const MIRList = () => {
 const user = useSelector((state) => state.auth?.userDetails || {});
 const [mirs, setMirs] = useState([]);
 const [selectedMIR, setSelectedMIR] = useState(null);
 const [showModal, setShowModal] = useState(false);
 const [loading, setLoading] = useState(false);
 const [previewingPdf, setPreviewingPdf] = useState(false);
 const [showSignModal, setShowSignModal] = useState(false);
 const [approveId, setApproveId] = useState(null);
 const [searchParams] = useSearchParams();
 const workOrderFromUrl = searchParams.get("workOrder") || "";
 const projectFromUrl = searchParams.get("project") || "";
 const statusFromUrl = searchParams.get("status") || "";

 const [filters, setFilters] = usePersistentFilters("mirs_filters", {
 search: "",
 status: statusFromUrl || "",
 fromDate: "",
 toDate: "",
 page: 1,
 limit: 10,
 });

 const [pagination, setPagination] = useState({
 currentPage: 1,
 totalPages: 1,
 totalCount: 0,
 limit: 10,
 hasNext: false,
 hasPrev: false,
 });
 const currentRole = String(user?.role?.name || user?.roleName || user?.role || "").toLowerCase();
 const canManageMIRs =
 currentRole === "admin" ||
 Boolean(
 user?.permissions?.some((permission) =>
 [
 "create:mirs",
 "edit:mirs",
 "approve:mirs",
 "reject:mirs",
 "delete:mirs",
 ].includes(permission)
 )
 );

 useEffect(() => {
 fetchMIRs();
 }, [
 filters.page,
 filters.limit,
 filters.search,
 filters.status,
 filters.fromDate,
 filters.toDate,
 workOrderFromUrl,
 statusFromUrl,
 ]);

 // ============================
 // DOWNLOAD PDF FUNCTION
 // ============================
 const handleDownloadPDF = async (id) => {
 try {
 const res = await api.get(`/mir/${id}/pdf`, { responseType: "blob" });

 const url = window.URL.createObjectURL(new Blob([res.data]));
 const link = document.createElement("a");
 link.href = url;
 link.download = `MIR-${id}.pdf`;
 link.click();
 } catch (err) {
 swal("Error", "Failed to download PDF", "error");
 }
 };
 const handlePreviewPDF = async (id) => {
 try {
 setPreviewingPdf(true);

 const res = await api.get(`/mir/${id}/pdf`, {
 responseType: "blob",
 });

 const fileURL = window.URL.createObjectURL(
 new Blob([res.data], { type: "application/pdf" }),
 );

 window.open(fileURL, "_blank");
 } catch (err) {
 console.error("MIR PDF preview error:", err);
 swal("Error", "Failed to preview MIR PDF", "error");
 } finally {
 setPreviewingPdf(false);
 }
 };

 const fetchMIRs = async () => {
 try {
 setLoading(true);

 const params = {
 page: filters.page,
 limit: filters.limit,
 sortBy: "createdAt",
 sortOrder: "desc",
 ...(filters.search && { search: filters.search }),
 ...(filters.fromDate && { fromDate: filters.fromDate }),
 ...(filters.toDate && { toDate: filters.toDate }),
 ...((filters.status || statusFromUrl) && {
 approvalStatus: filters.status || statusFromUrl,
 }),
 ...(workOrderFromUrl && { workOrder: workOrderFromUrl }),
 };

 const res = await api.get("/mir", { params });

 if (res.data.success && Array.isArray(res.data.data)) {
 setMirs(res.data.data);

 if (res.data.pagination) {
 setPagination((prev) => ({
 ...prev,
 currentPage: res.data.pagination.currentPage,
 totalPages: res.data.pagination.totalPages,
 totalCount: res.data.pagination.totalCount,
 hasNext: res.data.pagination.hasNextPage,
 hasPrev: res.data.pagination.hasPrevPage,
 }));
 }
 } else {
 setMirs([]);
 }
 } catch (err) {
 console.error("Error loading MIRs", err);
 swal("Error", "Failed to load MIRs", "error");
 } finally {
 setLoading(false);
 }
 };

 const handleDelete = async (id) => {
 const confirm = await swal({
 title: "Are you sure?",
 text: "This MIR will be deleted permanently!",
 icon: "warning",
 buttons: ["Cancel", "Delete"],
 dangerMode: true,
 });

 if (!confirm) return;

 try {
 const res = await api.delete(`/mir/${id}`);
 if (res.data.success) {
 swal("Deleted!", "MIR deleted successfully", "success");
 fetchMIRs();
 }
 } catch (err) {
 swal("Error", "Failed to delete MIR", "error");
 }
 };

 const handleView = async (id) => {
 try {
 const res = await api.get(`/mir/${id}`);
 if (res.data.success) {
 setSelectedMIR(res.data.data);
 setShowModal(true);
 }
 } catch (err) {
 swal("Error", "Failed to load MIR details", "error");
 }
 };

 const handleApprove = async (id) => {
 const confirm = await swal({
 title: "Approve MIR?",
 text: "Signature is required for approval.",
 icon: "info",
 buttons: ["Cancel", "Provide Signature"],
 });

 if (!confirm) return;

 setApproveId(id);
 setShowSignModal(true);
 };

 const handleSaveSignature = async (file) => {
 try {
 setLoading(true);

 const formData = new FormData();
 formData.append("signature", file);

 const res = await api.patch(`/mir/${approveId}/approve`, formData, {
 headers: {
 "Content-Type": "multipart/form-data",
 },
 });

 if (res.data.success) {
 swal("Approved!", "MIR approved successfully", "success");
 setShowSignModal(false);
 setApproveId(null);
 fetchMIRs();
 }
 } catch (err) {
 swal(
 "Error",
 err.response?.data?.message || "Failed to approve MIR",
 "error",
 );
 } finally {
 setLoading(false);
 }
 };

 const handleReject = async (id) => {
 const remarks = prompt("Enter rejection reason:");
 if (!remarks) return;

 try {
 const res = await api.patch(`/mir/${id}/reject`, { remarks });
 if (res.data.success) {
 swal("Rejected!", "MIR rejected", "success");
 fetchMIRs();
 }
 } catch (err) {
 swal("Error", "Failed to reject", "error");
 }
 };

 const handleConditionalApprove = async (id) => {
 const remarks = prompt("Enter conditional approval remarks:");
 if (!remarks) return;

 try {
 const res = await api.patch(`/mir/${id}/conditional-approve`, {
 remarks,
 });
 if (res.data.success) {
 swal("Success!", "MIR conditionally approved", "success");
 fetchMIRs();
 }
 } catch (err) {
 swal("Error", "Failed to approve", "error");
 }
 };

 const formatDate = (date) => {
 if (!date) return "---";
 return new Date(date).toLocaleDateString("en-IN", {
 day: "2-digit",
 month: "short",
 year: "numeric",
 });
 };

 const getStatusBadge = (status) => {
 switch (status) {
 case "Approved":
 return "success";
 case "Pending":
 return "warning";
 case "Rejected":
 return "danger";
 case "Conditional Approval":
 return "info";
 default:
 return "secondary";
 }
 };

 const getWorkOrderLabel = (mir) => {
 if (!mir) return "—";

 const wo = mir.workOrder;
 return wo?.woNo || wo?.woNumber || "—";
 };

 const getDeliveryName = (d) => {
 if (!d) return "N/A";
 return d.deliveryNumber || d.challanNumber || "N/A";
 };

 const svgMenu = (
 <svg width="20px" height="20px" viewBox="0 0 24 24">
 <circle fill="#000000" cx="5" cy="12" r="2"></circle>
 <circle fill="#000000" cx="12" cy="12" r="2"></circle>
 <circle fill="#000000" cx="19" cy="12" r="2"></circle>
 </svg>
 );
 return (
 <Fragment>
 <PageTitle activeMenu="MIR" motherMenu="Quality" />

 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title>Material Inspection Report (MIR) List</Card.Title>
 <PermissionWrapper user={user} required={["create:mirs"]}>
 <Link
 to={
 workOrderFromUrl
 ? `/mir/add?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`
 : "/mir/add"
 }
 className="btn btn-primary"
 >
 + Add MIR
 </Link>
 </PermissionWrapper>
 </Card.Header>

 <div className="d-flex flex-wrap align-items-end gap-2 px-4 py-3 border-bottom">
 <div className="d-flex flex-column" style={{ width: "180px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Search MIR</label>
 <input
 type="text"
 className="form-control form-control-sm"
 placeholder="Search MIR..."
 value={filters.search}
 onChange={(e) =>
 setFilters((p) => ({
 ...p,
 search: e.target.value,
 page: 1,
 }))
 }
 />
 </div>
 <div className="d-flex flex-column" style={{ width: "140px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Status</label>
 <select
 className="form-select form-select-sm"
 value={filters.status}
 onChange={(e) =>
 setFilters((p) => ({
 ...p,
 status: e.target.value,
 page: 1,
 }))
 }
 >
 <option value="">All Statuses</option>
 <option value="Pending">Pending</option>
 <option value="Approved">Approved</option>
 <option value="Rejected">Rejected</option>
 <option value="Conditional Approval">Conditional Approval</option>
 </select>
 </div>
 <div className="d-flex flex-column" style={{ width: "135px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">From Date</label>
 <input
 type="date"
 className="form-control form-control-sm"
 value={filters.fromDate}
 onChange={(e) =>
 setFilters((p) => ({
 ...p,
 fromDate: e.target.value,
 page: 1,
 }))
 }
 />
 </div>
 <div className="d-flex flex-column" style={{ width: "135px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">To Date</label>
 <input
 type="date"
 className="form-control form-control-sm"
 value={filters.toDate}
 onChange={(e) =>
 setFilters((p) => ({
 ...p,
 toDate: e.target.value,
 page: 1,
 }))
 }
 />
 </div>
 <div className="ms-auto d-flex flex-column" style={{ width: "65px" }}>
 <label className="text-muted mb-1 fs-12 font-weight-bold">Show</label>
 <select
 className="form-select form-select-sm"
 value={filters.limit}
 onChange={(e) =>
 setFilters((p) => ({
 ...p,
 limit: Number(e.target.value),
 page: 1,
 }))
 }
 >
 <option value={10}>10</option>
 <option value={25}>25</option>
 <option value={50}>50</option>
 </select>
 </div>
 </div>

 <Card.Body>
 {loading ? (
 <div className="text-center py-5">
 <div className="spinner-border text-primary"></div>
 <p className="mt-3">Loading...</p>
 </div>
 ) : mirs.length === 0 ? (
 <div className="text-center py-5">
 <p>No MIRs found.</p>
 <Link
 to={
 workOrderFromUrl
 ? `/mir/add?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`
 : "/mir/add"
 }
 className="btn btn-primary"
 >
 + Add MIR
 </Link>
 </div>
 ) : (
 <div className="table-responsive">
 <Table hover className="align-middle">
 <thead>
 <tr>
 <th>#</th>
 <th>MIR Number</th>
 <th>Material(s)</th>
 <th>Work Order</th>
 <th>Delivery Ref</th>
 <th>Status</th>
 <th>Date</th>
 <th className="text-center">Verified Copy</th>
 <th className="text-center">Actions</th>
 </tr>
 </thead>

 <tbody>
 {mirs.map((mir, i) => (
 <tr key={mir._id}>
 <td>
 {(pagination.currentPage - 1) * pagination.limit +
 (i + 1)}
 </td>

 <td className="text-nowrap font-weight-bold">{mir.mirNumber}</td>

 <td style={{ minWidth: "300px", maxWidth: "450px" }}>
 <div style={{ 
 maxHeight: "120px", 
 overflowY: "auto", 
 paddingRight: "8px",
 scrollbarWidth: "thin" 
 }}>
 <ul className="list-unstyled mb-0">
 {mir.items?.map((it, idx) => (
 <li key={idx} className="mb-2 border-bottom pb-1 last-child-no-border">
 <div className="d-flex justify-content-between align-items-start">
 <div style={{ flex: 1 }}>
 {it.wlino && (
 <Badge bg="primary" className="me-2 mb-1 rounded-pill" style={{ fontSize: "0.7rem" }}>
 WLI-{it.wlino}
 </Badge>
 )}
 <span style={{ fontSize: "0.9rem", fontWeight: "500", display: "block", lineHeight: "1.2" }}>
 {it.materialName}
  {it.subItems && it.subItems.length > 0 && (
    <div className="mt-1 ps-2 text-muted" style={{ fontSize: "0.75rem" }}>
      {it.subItems.map((s, sIdx) => (
        <div key={sIdx}>
          ↳ {s.name} ({s.quantity} {s.unit || it.unit || ""}) {s.remarks ? `- ${s.remarks}` : ""}
        </div>
      ))}
    </div>
  )}
 </span>
 </div>
 <Badge bg="info" className="ms-2 rounded-pill" style={{ fontSize: "0.75rem" }}>
 {it.quantityInspected} {it.unit}
 </Badge>
 </div>
 </li>
 )) || (
 <li className="mb-1">
 <strong>{mir.materialName}</strong>
 <div className="text-muted small">
 {it.quantityInspected} {it.unit}
 </div>
 </li>
 )}
 </ul>
 </div>
 </td>

 <td style={{ fontSize: "0.9rem" }}>{getWorkOrderLabel(mir)}</td>
 <td style={{ fontSize: "0.9rem" }}>{getDeliveryName(mir.deliveryReference)}</td>

 <td>
 <Badge bg={getStatusBadge(mir.approvalStatus)} className="rounded-pill">
 {mir.approvalStatus}
 </Badge>
 </td>

 <td>{formatDate(mir.inspectionDate)}</td>
 <td className="text-center">
 <VerifiedCopyActions 
 id={mir._id} 
 type="mir" 
 hasVerifiedCopy={!!mir.verifiedCopy?.filePath} 
 verifiedCopy={mir.verifiedCopy}
 onUploadSuccess={fetchMIRs}
 canUpload={canManageMIRs}
 />
 </td>
 <td className="text-center">
 <Dropdown>
 <Dropdown.Toggle
 variant=""
 as="div"
 className="btn i-false p-0 cursor-pointer"
 title="Click for more actions"
 >
 {svgMenu}
 </Dropdown.Toggle>

 <Dropdown.Menu align="end" as={PortalMenu}>
 <PermissionWrapper user={user} required={["read:mirs"]}>
 <Dropdown.Item
 onClick={() => handleView(mir._id)}
 title="View full MIR details"
 >
 <i className="fas fa-eye me-2 text-primary"></i>View Details
 </Dropdown.Item>
 </PermissionWrapper>

 <PermissionWrapper user={user} required={["read:mirs"]}>
 {mir.inspectionReport?.fileUrl && (
 <Dropdown.Item
 href={getFileUrl(mir.inspectionReport.fileUrl)}
 target="_blank"
 rel="noopener noreferrer"
 title="View uploaded inspection report"
 >
 <i className="fas fa-file-alt me-2 text-info"></i>
 View Report
 </Dropdown.Item>
 )}

 {mir.clientApproval?.fileUrl && (
 <Dropdown.Item
 href={getFileUrl(mir.clientApproval.fileUrl)}
 target="_blank"
 rel="noopener noreferrer"
 title="View uploaded client approval"
 >
 <i className="fas fa-file-signature me-2 text-warning"></i>
 View Client Approval
 </Dropdown.Item>
 )}
 </PermissionWrapper>

 <PermissionWrapper user={user} required={["edit:mirs"]}>
 <Dropdown.Item
 as={Link}
 to={
 workOrderFromUrl
 ? `/mir/edit/${mir._id}?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`
 : `/mir/edit/${mir._id}`
 }
 disabled={mir.approvalStatus !== "Pending"}
 title="Edit MIR (only if Pending)"
 >
 <i className="fas fa-edit me-2 text-warning"></i>Edit
 </Dropdown.Item>
 </PermissionWrapper>

 <PermissionWrapper user={user} required={["read:mirs"]}>
 <Dropdown.Item
 onClick={() => handlePreviewPDF(mir._id)}
 disabled={previewingPdf}
 title="Preview system-generated MIR PDF"
 >
 <i className="fas fa-eye me-2 text-info"></i>
 Preview PDF
 </Dropdown.Item>
 <Dropdown.Item
 onClick={() => handleDownloadPDF(mir._id)}
 title="Download system-generated MIR PDF"
 >
 <i className="fas fa-file-pdf me-2 text-danger"></i>
 Download PDF
 </Dropdown.Item>
 </PermissionWrapper>

 {mir.approvalStatus === "Pending" && (
 <>
 <PermissionWrapper user={user} required={["approve:mirs"]}>
 <Dropdown.Item
 onClick={() => handleApprove(mir._id)}
 className="text-success"
 >
 <i className="fas fa-check me-2"></i>Approve
 </Dropdown.Item>

 <Dropdown.Item
 onClick={() =>
 handleConditionalApprove(mir._id)
 }
 className="text-info"
 >
 <i className="fas fa-check-circle me-2"></i>
 Conditional Approve
 </Dropdown.Item>
 </PermissionWrapper>

 <PermissionWrapper user={user} required={["reject:mirs"]}>
 <Dropdown.Item
 onClick={() => handleReject(mir._id)}
 className="text-danger"
 >
 <i className="fas fa-times me-2"></i>Reject
 </Dropdown.Item>
 </PermissionWrapper>
 </>
 )}

 <Dropdown.Divider />

 <PermissionWrapper user={user} required={["delete:mirs"]}>
 <Dropdown.Item
 className="text-danger"
 onClick={() => handleDelete(mir._id)}
 >
 <i className="fas fa-trash me-2"></i>Delete
 </Dropdown.Item>
 </PermissionWrapper>
 </Dropdown.Menu>
 </Dropdown>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>

 <AppPagination
 currentPage={filters.page}
 totalPages={pagination.totalPages}
 totalCount={pagination.totalCount}
 itemsLength={mirs.length}
 limit={filters.limit}
 onPageChange={(pageNumber) => setFilters((prev) => ({ ...prev, page: pageNumber }))}
 />
 </div>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 {/* ====================== MODAL ====================== */}
 <Modal
 show={showModal}
 onHide={() => setShowModal(false)}
 centered
 size="lg"
 >
 <Modal.Header closeButton>
 <Modal.Title>MIR Details</Modal.Title>
 </Modal.Header>

 <Modal.Body>
 {selectedMIR && (
 <>
 <p>
 <strong>MIR Number:</strong> {selectedMIR.mirNumber}
 </p>
 <p>
 <strong>Work Order:</strong> {getWorkOrderLabel(selectedMIR)}
 </p>

 <p>
 <strong>Delivery:</strong>{" "}
 {getDeliveryName(selectedMIR.deliveryReference)}
 </p>

 <p>
 <strong>Status:</strong>
 <Badge
 bg={getStatusBadge(selectedMIR.approvalStatus)}
 className="ms-2 rounded-pill"
 >
 {selectedMIR.approvalStatus}
 </Badge>
 </p>

 <hr />

 <h5>Materials</h5>
 <ul className="ps-3 mb-2">
 {selectedMIR.items?.map((it, i) => (
 <li key={i}>
 <strong>{it.materialName}</strong> — {it.quantityInspected}{" "}
 {it.unit}
 </li>
 ))}
 </ul>

 <hr />

 {selectedMIR.remarks && (
 <>
 <h5>Remarks</h5>
 <p className="text-muted">{selectedMIR.remarks}</p>
 </>
 )}

 <hr />

 <div className="row">
 <div className="col-md-6">
 <p>
 <strong>Created:</strong>{" "}
 {formatDate(selectedMIR.createdAt)}
 </p>
 </div>
 <div className="col-md-6">
 <p>
 <strong>Updated:</strong>{" "}
 {formatDate(selectedMIR.updatedAt)}
 </p>
 </div>
 </div>

 {selectedMIR.inspectionReport?.filePath && (
 <>
 <hr />
 <h5>Inspection Report</h5>
 <FilePreview 
 file={{
 fileName: selectedMIR.inspectionReport.fileName,
 fileUrl: selectedMIR.inspectionReport.filePath
 }}
 onRemove={async () => {
 const confirm = await swal({
 title: "Delete Report?",
 text: "This will remove the inspection report file.",
 icon: "warning",
 buttons: ["Cancel", "Delete"],
 dangerMode: true,
 });
 if (!confirm) return;
 try {
 const res = await api.patch(`/mir/${selectedMIR._id}/remove-file`, { field: 'inspectionReport' });
 if (res.data.success) {
 swal("Deleted!", "Report removed", "success");
 handleView(selectedMIR._id); // Refresh modal
 }
 } catch (err) {
 swal("Error", "Failed to remove report", "error");
 }
 }}
 />
 </>
 )}

 {selectedMIR.clientApproval?.filePath && (
 <>
 <hr />
 <h5>Client Approval</h5>
 <FilePreview 
 file={{
 fileName: selectedMIR.clientApproval.fileName,
 fileUrl: selectedMIR.clientApproval.filePath
 }}
 onRemove={async () => {
 const confirm = await swal({
 title: "Delete Approval?",
 text: "This will remove the client approval file.",
 icon: "warning",
 buttons: ["Cancel", "Delete"],
 dangerMode: true,
 });
 if (!confirm) return;
 try {
 const res = await api.patch(`/mir/${selectedMIR._id}/remove-file`, { field: 'clientApproval' });
 if (res.data.success) {
 swal("Deleted!", "Approval removed", "success");
 handleView(selectedMIR._id); // Refresh modal
 }
 } catch (err) {
 swal("Error", "Failed to remove approval", "error");
 }
 }}
 />
 </>
 )}
 </>
 )}
 </Modal.Body>

 <Modal.Footer>
 <Button variant="secondary" onClick={() => setShowModal(false)}>
 Close
 </Button>

 {selectedMIR && (
 <>
 <Button
 variant="danger"
 onClick={() => handleDownloadPDF(selectedMIR._id)}
 >
 <i className="fas fa-file-pdf me-2"></i>Download PDF
 </Button>

 <Link
 className="btn btn-primary"
 to={
 workOrderFromUrl
 ? `/mir/edit/${selectedMIR._id}?workOrder=${workOrderFromUrl}`
 : `/mir/edit/${selectedMIR._id}`
 }
 >
 Edit MIR
 </Link>
 </>
 )}
 </Modal.Footer>
 </Modal>
 {/* =========================
 PDF PREVIEW LOADER
========================== */}
 {previewingPdf && (
 <div
 className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
 style={{
 background: "rgba(0,0,0,0.4)",
 zIndex: 9999,
 }}
 >
 <div className="bg-white p-4 rounded shadow text-center">
 <div className="spinner-border text-primary mb-3"></div>
 <h6 className="mb-0">Preparing MIR PDF…</h6>
 </div>
 </div>
 )}

 <SignatureModal
 show={showSignModal}
 onHide={() => {
 setShowSignModal(false);
 setApproveId(null);
 }}
 onSave={handleSaveSignature}
 title="Client Signature for MIR Approval"
 />
 </Fragment>
 );
};

export default MIRList;
